import Vue from 'vue'
import Vuex from 'vuex'
import httpService from '@/services/http'

Vue.use(Vuex)

// 從 localStorage 讀取購物車數據
const loadCartItems = () => {
  const savedCart = localStorage.getItem('cartItems')
  return savedCart ? JSON.parse(savedCart) : []
}

const state = {
  customer: JSON.parse(localStorage.getItem('customer')) || null,
  token: localStorage.getItem('token') || null,
  user: JSON.parse(localStorage.getItem('user')) || null,
  userToken: localStorage.getItem('user_token') || null,
  storeInfo: null,
  categories: [],
  products: [],
  news: [],
  cartItems: [],
  shippingInfo: null,
  orderInfo: null,
  invoiceInfo: null,
  shippingMethod: null,
  paymentMethod: null
}

const mutations = {
  addToCart(state, { product, variation, quantity = 1 }) {
    const existingItem = state.cartItems.find(
      item => item.id === product.id && item.product_variation_id === variation.id
    )

    if (existingItem) {
      existingItem.quantity += quantity
    } else {
      state.cartItems.push({
        id: product.id,
        product_name: product.name,
        price: product.salePrice || product.price,
        product_image: product.image_url,
        product_variation_id: variation.id,
        variation_name: variation.name,
        quantity
      })
    }
  },
  removeFromCart(state, item) {
    const index = state.cartItems.findIndex(i => 
      i.id === item.id && 
      i.product_variation_id === item.product_variation_id
    )
    if (index > -1) {
      state.cartItems.splice(index, 1)
    }
  },
  updateCartItemQuantity(state, { item, quantity }) {
    const cartItem = state.cartItems.find(i => 
      i.id === item.id && 
      i.product_variation_id === item.product_variation_id
    )
    if (cartItem) {
      cartItem.quantity = quantity
    }
  },
  setCartItems(state, items) {
    state.cartItems = items
  },
  clearCart(state) {
    state.cartItems = []
  },
  setShippingInfo(state, info) {
    state.shippingInfo = info
  },
  setOrderInfo(state, info) {
    state.orderInfo = info
  },
  setInvoiceInfo(state, info) {
    state.invoiceInfo = info
  },
  setShippingMethod(state, method) {
    state.shippingMethod = method
  },
  setPaymentMethod(state, method) {
    state.paymentMethod = method
  },
  setCategories(state, categories) {
    state.categories = categories
  },
  setNews(state, news) {
    state.news = news
  },
  SET_STORE_INFO(state, info) {
    state.storeInfo = info
  },
  SET_TOKEN(state, token) {
    state.token = token
    if (token) {
      localStorage.setItem('token', token)
    } else {
      localStorage.removeItem('token')
    }
  },
  SET_CUSTOMER(state, customer) {
    state.customer = customer
    if (customer) {
      localStorage.setItem('customer', JSON.stringify(customer))
    } else {
      localStorage.removeItem('customer')
    }
  },
  SET_PRODUCTS(state, products) {
    state.products = products
  },
  SET_CATEGORIES(state, categories) {
    state.categories = categories
  },
  SET_NEWS(state, news) {
    state.news = news
  },
  SET_USER(state, user) {
    state.user = user
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  },
  SET_USER_TOKEN(state, token) {
    state.userToken = token
    if (token) {
      localStorage.setItem('user_token', token)
    } else {
      localStorage.removeItem('user_token')
    }
  }
}

const actions = {
  async fetchStoreInfo({ commit }) {
    try {
      const response = await httpService.get('/api/v1/stores/current')
      const data = await response.json()
      commit('SET_STORE_INFO', data.store)
    } catch (error) {
      console.error('Error fetching store info:', error)
    }
  },
  async fetchProducts({ commit }, categoryId = null) {
    try {
      let url = '/api/v1/products'
      if (categoryId) {
        url += `?category=${categoryId}`
      }
      const response = await httpService.get(url)
      const data = await response.json()
      commit('SET_PRODUCTS', data.products)
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  },
  async fetchCategories({ commit }) {
    try {
      const response = await httpService.get('/api/v1/categories')
      const data = await response.json()
      commit('SET_CATEGORIES', data.categories)
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  },
  async fetchNews({ commit }) {
    try {
      const response = await httpService.get('/api/v1/news')
      const data = await response.json()
      commit('SET_NEWS', data.news)
    } catch (error) {
      console.error('Error fetching news:', error)
    }
  },
  async fetchCustomer({ commit, state }) {
    if (!state.token) return
    try {
      const response = await httpService.get('/api/v1/customers/current')
      const data = await response.json()
      commit('SET_CUSTOMER', data)
    } catch (error) {
      console.error('Error fetching customer:', error)
      if (error.response?.status === 401) {
        commit('SET_TOKEN', null)
        commit('SET_CUSTOMER', null)
        localStorage.removeItem('token')
        localStorage.removeItem('customer')
      }
    }
  },
  async login({ commit, dispatch }, credentials) {
    const response = await httpService.post('/api/v1/customers/sign_in', {
      api_v1_customer: credentials
    })
    const data = await response.json()
    const token = data.token || response.headers.get('authorization')?.split(' ')[1]
    if (token) {
      commit('SET_TOKEN', token)
      await dispatch('handleLoginSuccess', data.data)
      return data.data
    }
    throw new Error('No token received')
  },
  async register({ commit, dispatch }, data) {
    const response = await httpService.post('/api/v1/customers', {
      customer: data
    })
    
    const responseData = await response.json()
    // 從 response body 或 header 中取得 token
    const token = responseData.token || response.headers.get('authorization')?.split(' ')[1]
    
    if (token) {
      commit('SET_TOKEN', token)
      await dispatch('handleLoginSuccess', responseData.data)
      return responseData.data
    }
    throw new Error('No token received')
  },
  logout({ commit, dispatch }) {
    dispatch('handleLogout')
  },
  async createLiCoinWallet({ commit }) {
    const response = await httpService.post('/api/v1/li_coin/wallet')
    const data = await response.json()
    return data
  },
  async fetchLiCoinWallet({ commit }) {
    const response = await httpService.get('/api/v1/li_coin/wallet')
    const data = await response.json()
    return data
  },
  async mintLiCoin({ commit }, amount) {
    const response = await httpService.post('/api/v1/li_coin/mint', { amount })
    const data = await response.json()
    return data
  },
  async checkLiCoinBalance({ commit, state }, amount) {
    const response = await httpService.get('/api/v1/li_coin/wallet')
    const data = await response.json()
    const walletData = data.wallet
    const currentBalance = parseFloat(walletData.licBalance)
    return currentBalance >= amount
  },
  async transferLiCoin({ commit }, { amount, orderInfo }) {
    const requestData = {
      amount: amount,
      order_info: orderInfo
    }
    const response = await httpService.post('/api/v1/li_coin/transfer', requestData)
    const data = await response.json()
    return data
  },
  async fetchCartItems({ commit, state }) {
    if (!state.token) {
      console.log('fetchCartItems: No token, skipping fetch')
      return
    }
    
    try {
      console.log('fetchCartItems: Fetching cart items from server')
      const response = await httpService.get('/api/v1/cart')
      const data = await response.json()
      console.log('fetchCartItems: Server response:', data)
      commit('setCartItems', data.items)
      console.log('fetchCartItems: Cart items updated in state')
    } catch (error) {
      console.error('Error fetching cart items:', error)
    }
  },
  async syncLocalCartToServer({ commit, state, dispatch }) {
    if (!state.token) {
      console.log('syncLocalCartToServer: No token, skipping sync')
      return
    }
    
    const localCart = loadCartItems()
    console.log('syncLocalCartToServer: Local cart items:', localCart)
    
    if (localCart.length === 0) {
      console.log('syncLocalCartToServer: No local cart items to sync')
      return
    }

    try {
      console.log('syncLocalCartToServer: Starting sync of', localCart.length, 'items')
      
      // 批次同步 localStorage 的購物車到後端
      for (const item of localCart) {
        console.log('syncLocalCartToServer: Syncing item:', item)
        await httpService.post('/api/v1/cart/items', {
          cart_item: {
            product_id: item.id,
            product_variation_id: item.product_variation_id,
            quantity: item.quantity
          }
        })
      }
      
      // 同步完成後清空 localStorage
      localStorage.removeItem('cartItems')
      console.log('syncLocalCartToServer: Sync completed, localStorage cleared')
      
      // 注意：不在此處調用 fetchCartItems，由 handleLoginSuccess 處理
    } catch (error) {
      console.error('Error syncing local cart to server:', error)
    }
  },
  async addToCart({ commit, state, dispatch }, { product, variation, quantity = 1 }) {
    if (state.token) {
      // 已登入：呼叫 API
      try {
        const response = await httpService.post('/api/v1/cart/items', {
          cart_item: {
            product_id: product.id,
            product_variation_id: variation.id,
            quantity: quantity
          }
        })
        // 重新取得購物車資料
        await dispatch('fetchCartItems')
      } catch (error) {
        console.error('Error adding to cart:', error)
        throw error
      }
    } else {
      // 未登入：操作 localStorage
      const localCart = loadCartItems()
      const existingItem = localCart.find(
        item => item.id === product.id && item.product_variation_id === variation.id
      )

      if (existingItem) {
        existingItem.quantity += quantity
      } else {
        localCart.push({
          id: product.id,
          product_name: product.name,
          price: product.salePrice || product.price,
          product_image: product.image_url,
          product_variation_id: variation.id,
          variation_name: variation.name,
          quantity
        })
      }
      
      localStorage.setItem('cartItems', JSON.stringify(localCart))
      commit('setCartItems', localCart)
    }
  },
  async removeFromCart({ commit, state, dispatch }, item) {
    if (state.token) {
      // 已登入：呼叫 API
      try {
        await httpService.delete(`/api/v1/cart/items/${item.id}`)
        // 重新取得購物車資料
        await dispatch('fetchCartItems')
      } catch (error) {
        console.error('Error removing from cart:', error)
        throw error
      }
    } else {
      // 未登入：操作 localStorage
      const localCart = loadCartItems()
      const index = localCart.findIndex(i => 
        i.id === item.id && 
        i.product_variation_id === item.product_variation_id
      )
      if (index > -1) {
        localCart.splice(index, 1)
        localStorage.setItem('cartItems', JSON.stringify(localCart))
        commit('setCartItems', localCart)
      }
    }
  },
  async updateCartItemQuantity({ commit, state, dispatch }, { item, quantity }) {
    if (state.token) {
      // 已登入：呼叫 API
      try {
        await httpService.patch(`/api/v1/cart/items/${item.id}`, {
          cart_item: {
            quantity: quantity
          }
        })
        // 重新取得購物車資料
        await dispatch('fetchCartItems')
      } catch (error) {
        console.error('Error updating cart item quantity:', error)
        throw error
      }
    } else {
      // 未登入：操作 localStorage
      const localCart = loadCartItems()
      const cartItem = localCart.find(i => 
        i.id === item.id && 
        i.product_variation_id === item.product_variation_id
      )
      if (cartItem) {
        cartItem.quantity = quantity
        localStorage.setItem('cartItems', JSON.stringify(localCart))
        commit('setCartItems', localCart)
      }
    }
  },
  async initCart({ commit, state, dispatch }) {
    if (state.token) {
      // 已登入：從 API 取得購物車
      try {
        await dispatch('fetchCartItems')
      } catch (error) {
        console.error('Error fetching cart from API, falling back to local cart:', error)
        // API 調用失敗時，回退到本地購物車
        const localCart = loadCartItems()
        commit('setCartItems', localCart)
      }
    } else {
      // 未登入：從 localStorage 載入
      const localCart = loadCartItems()
      commit('setCartItems', localCart)
    }
  },
  async handleLoginSuccess({ commit, dispatch }, customerData) {
    console.log('handleLoginSuccess: Starting login success handler', customerData)
    commit('SET_CUSTOMER', customerData)
    
    // 同步 localStorage 購物車到後端
    console.log('handleLoginSuccess: Syncing local cart to server')
    await dispatch('syncLocalCartToServer')
    
    // 確保購物車狀態是最新的
    if (customerData) {
      console.log('handleLoginSuccess: Fetching updated cart items')
      await dispatch('fetchCartItems')
    }
    console.log('handleLoginSuccess: Login success handler completed')
  },
  async handleLogout({ commit }) {
    commit('SET_TOKEN', null)
    commit('SET_CUSTOMER', null)
    commit('clearCart') // 清空購物車
    
    // 清除 localStorage
    localStorage.removeItem('token')
    localStorage.removeItem('customer')
  },
  async userLogout({ commit }) {
    // 清除 User 相關的狀態和 localStorage
    commit('SET_USER_TOKEN', null)
    commit('SET_USER', null)
    
    // 清除 localStorage
    localStorage.removeItem('user_token')
    localStorage.removeItem('user')
  },
  // 商家後台 API Actions
  async fetchStoreDashboard({ commit, state }, params = {}) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    params.store_id = storeId
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = `/api/v1/store/dashboard?${queryString}`
      const response = await httpService.get(url)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching store dashboard:', error)
      throw error
    }
  },
  async fetchStoreProducts({ commit, state }, params = {}) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    params.store_id = storeId
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = `/api/v1/store/products?${queryString}`
      const response = await httpService.get(url)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching store products:', error)
      throw error
    }
  },

  // 導航列管理相關 actions
  async fetchStoreNavigationItems({ commit, state }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.get('/api/v1/store/navigation_items')
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching navigation items:', error)
      throw error
    }
  },

  async createStoreNavigationItem({ commit, state }, { navigationItemData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.post('/api/v1/store/navigation_items', {
        navigation_item: navigationItemData
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating navigation item:', error)
      throw error
    }
  },

  async updateStoreNavigationItem({ commit, state }, { id, navigationItemData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.put(`/api/v1/store/navigation_items/${id}`, {
        navigation_item: navigationItemData
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating navigation item:', error)
      throw error
    }
  },

  async deleteStoreNavigationItem({ commit, state }, { id }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.delete(`/api/v1/store/navigation_items/${id}`)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error deleting navigation item:', error)
      throw error
    }
  },

  async reorderStoreNavigationItems({ commit, state }, { items }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.post('/api/v1/store/navigation_items/reorder', { items })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error reordering navigation items:', error)
      throw error
    }
  },

  async toggleStoreNavigationItemStatus({ commit, state }, { id }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    try {
      const response = await httpService.patch(`/api/v1/store/navigation_items/${id}/toggle_status`)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error toggling navigation item status:', error)
      throw error
    }
  },
  async createStoreProduct({ commit, state }, productData) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('product[store_id]', storeId)
    formData.append('product[name]', productData.name)
    formData.append('product[description]', productData.description || '')
    formData.append('product[price]', productData.price)
    formData.append('product[sale_price]', productData.sale_price || '')
    formData.append('product[category_id]', productData.category_id)
    formData.append('product[active]', productData.active)
    
    // 如果有圖片文件，添加到 FormData
    if (productData.image instanceof File) {
      formData.append('product[image]', productData.image)
    }
    
    // 處理標籤
    if (productData.labels && productData.labels.length > 0) {
      productData.labels.forEach((label, index) => {
        formData.append(`labels[${index}]`, label)
      })
    }
    
    try {
      console.log('Creating product with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.post('/api/v1/store/products', formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating store product:', error)
      throw error
    }
  },
  async updateStoreProduct({ commit, state }, { id, productData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('product[store_id]', storeId)
    formData.append('product[name]', productData.name)
    formData.append('product[description]', productData.description || '')
    formData.append('product[price]', productData.price)
    formData.append('product[sale_price]', productData.sale_price || '')
    formData.append('product[category_id]', productData.category_id)
    formData.append('product[active]', productData.active)
    
    // 如果有圖片文件，添加到 FormData
    if (productData.image instanceof File) {
      formData.append('product[image]', productData.image)
    }
    
    // 處理標籤
    if (productData.labels && productData.labels.length > 0) {
      productData.labels.forEach((label, index) => {
        formData.append(`labels[${index}]`, label)
      })
    }
    
    try {
      console.log('Updating product with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.patch(`/api/v1/store/products/${id}`, formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating store product:', error)
      throw error
    }
  },
  async deleteStoreProduct({ commit, state }, id) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    try {
      const url = `/api/v1/store/products/${id}?store_id=${storeId}`
      const response = await httpService.delete(url)
      return response
    } catch (error) {
      console.error('Error deleting store product:', error)
      throw error
    }
  },
  async fetchStoreOrders({ commit, state }, params = {}) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    params.store_id = storeId
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = `/api/v1/store/orders?${queryString}`
      const response = await httpService.get(url)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching store orders:', error)
      throw error
    }
  },
  async updateStoreOrder({ commit, state }, { id, orderData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    const payload = { ...orderData, store_id: storeId }
    try {
      const response = await httpService.patch(`/api/v1/store/orders/${id}`, {
        order: payload
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating store order:', error)
      throw error
    }
  },
  async fetchStoreCustomers({ commit, state }, params = {}) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    params.store_id = storeId
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = `/api/v1/store/customers?${queryString}`
      const response = await httpService.get(url)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching store customers:', error)
      throw error
    }
  },
  async fetchStoreLabels({ commit, state }, params = {}) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    params.store_id = storeId
    try {
      const queryString = new URLSearchParams(params).toString()
      const url = `/api/v1/store/labels?${queryString}`
      const response = await httpService.get(url)
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error fetching store labels:', error)
      throw error
    }
  },
  async createStoreLabel({ commit, state }, labelData) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    const payload = { ...labelData, store_id: storeId }
    try {
      const response = await httpService.post('/api/v1/store/labels', {
        label: payload
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating store label:', error)
      throw error
    }
  },
  async updateStoreLabel({ commit, state }, { id, labelData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    const payload = { ...labelData, store_id: storeId }
    try {
      const response = await httpService.patch(`/api/v1/store/labels/${id}`, {
        label: payload
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating store label:', error)
      throw error
    }
  },
  async deleteStoreLabel({ commit, state }, id) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    try {
      const url = `/api/v1/store/labels/${id}?store_id=${storeId}`
      const response = await httpService.delete(url)
      return response
    } catch (error) {
      console.error('Error deleting store label:', error)
      throw error
    }
  },
  async createStoreCategory({ commit, state }, categoryData) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('category[name]', categoryData.name)
    formData.append('category[description]', categoryData.description || '')
    
    // 如果有圖片文件，添加到 FormData
    if (categoryData.image instanceof File) {
      formData.append('category[image]', categoryData.image)
    }
    
    try {
      console.log('Creating category with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.post('/api/v1/store/categories', formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating store category:', error)
      throw error
    }
  },
  async updateStoreCategory({ commit, state }, { id, categoryData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('category[name]', categoryData.name)
    formData.append('category[description]', categoryData.description || '')
    
    // 如果有圖片文件，添加到 FormData
    if (categoryData.image instanceof File) {
      formData.append('category[image]', categoryData.image)
    }
    
    try {
      console.log('Updating category with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.patch(`/api/v1/store/categories/${id}`, formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating store category:', error)
      throw error
    }
  },
  async deleteStoreCategory({ commit, state }, id) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    try {
      const response = await httpService.delete(`/api/v1/store/categories/${id}`)
      return response
    } catch (error) {
      console.error('Error deleting store category:', error)
      throw error
    }
  },
  async createStoreNews({ commit, state }, newsData) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('news[store_id]', storeId)
    formData.append('news[title]', newsData.title)
    formData.append('news[content]', newsData.content)
    formData.append('news[publish_date]', newsData.publish_date)
    
    // 如果有圖片文件，添加到 FormData
    if (newsData.image instanceof File) {
      formData.append('news[image]', newsData.image)
    }
    
    try {
      console.log('Creating news with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.post('/api/v1/store/news', formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating store news:', error)
      throw error
    }
  },
  async updateStoreNews({ commit, state }, { id, newsData }) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    
    // 處理文件上傳
    const formData = new FormData()
    formData.append('news[title]', newsData.title)
    formData.append('news[content]', newsData.content)
    formData.append('news[publish_date]', newsData.publish_date)
    
    // 如果有圖片文件，添加到 FormData
    if (newsData.image instanceof File) {
      formData.append('news[image]', newsData.image)
    }
    
    try {
      console.log('Updating news with FormData:', formData)
      // 調試：打印 FormData 內容
      for (let [key, value] of formData.entries()) {
        console.log('FormData entry:', key, value)
      }
      
      const response = await httpService.patch(`/api/v1/store/news/${id}`, formData, {
        headers: {
          // 讓瀏覽器自動設置 Content-Type 為 multipart/form-data
        }
      })
      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating store news:', error)
      throw error
    }
  },
  async deleteStoreNews({ commit, state }, id) {
    const storeId = (state.storeInfo && state.storeInfo.id) || (state.user && state.user.stores && state.user.stores[0] && state.user.stores[0].id)
    if (!storeId) throw new Error('store_id 不存在')
    try {
      const response = await httpService.delete(`/api/v1/store/news/${id}`)
      return response
    } catch (error) {
      console.error('Error deleting store news:', error)
      throw error
    }
  }
}

const getters = {
  getProductsByCategory: (state) => (categoryName) => {
    return Array.isArray(state.products) ? state.products.filter(product => product.category_name === categoryName) : []
  },
  getNewArrivals: (state) => {
    return Array.isArray(state.products) ? state.products.filter(product => product.labels && product.labels.includes('NEW')) : []
  },
  getSaleProducts: (state) => {
    return Array.isArray(state.products) ? state.products.filter(product => product.labels && product.labels.includes('SALE')) : []
  },
  getProductById: (state) => (id) => {
    return Array.isArray(state.products) ? state.products.find(product => product.id === id) : null
  },
  cartItems: state => state.cartItems,
  cartTotal: state => {
    if (!Array.isArray(state.cartItems) || state.cartItems.length === 0) {
      return 0
    }
    return state.cartItems.reduce((total, item) => {
      const price = parseFloat(item.price) || 0
      const quantity = parseInt(item.quantity) || 0
      return total + (price * quantity)
    }, 0)
  },
  isAuthenticated: state => !!state.token,
  isUserAuthenticated: state => !!state.userToken,
  currentCustomer: state => state.customer,
  currentUser: state => state.user,
  storeInfo: state => state.storeInfo
}

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters
}) 